import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import M from 'materialize-css';
import SelectCountry from '../generalComponets/SelectCountry';
import RadioButton from '../general/RadioButton';
import ProfileItem from '../profiles/ProfileItem';
import SelectState from '../general/SelectState';
import { useProfiles } from '../../hooks/useProfiles';

const SelectUsers = forwardRef(({ showQuestionStatus, triviaState, propagateOptionAll }, ref) => {
  const options = useMemo(() => ['All', 'Country', 'Users'], []);
  const {
    filteredProfiles,
    selectProfiles,
    selectProfile,
    filterProfilesByCountry,
    filterProfilesByCountryAndState,
    filterProfileByEmailAndName,
    resetFilterProfiles,
    cleanFilters,
    loadProfileSignedUp,
    cleanSelectProfiles,
    getFilteredProfiles,
  } = useProfiles();
  const [country, setCountry] = useState(null);
  const [optionSendTo, setOptionSendTo] = useState(options[2]);
  const [searchName, setSearchName] = useState('');
  const [forceUpdate, setForceUpdate] = useState(0);
  const [state, setState] = useState(null);
  const [allState, setAllState] = useState(true);
  const messageRef = React.createRef();
  const childRef = useRef();

  useEffect(() => {
    M.AutoInit();
    if (messageRef.current) {
      M.textareaAutoResize(messageRef.current);
    }
  }, [messageRef]);

  useEffect(() => {
    loadProfileSignedUp();
  }, [loadProfileSignedUp]);

  useEffect(() => {
    setAllState(true);
    setState(null);
    if (optionSendTo === options[0]) {
      resetFilterProfiles();
    } else {
      cleanFilters();
    }
  }, [optionSendTo, cleanFilters, resetFilterProfiles, options]);

  useEffect(() => {
    if (country && state) {
      filterProfilesByCountryAndState(country, state);
      cleanSelectProfiles();
    } else if (allState && country && country !== '' && !state) {
      filterProfilesByCountry(country);
    }
  }, [allState, country, state, filterProfilesByCountryAndState, cleanSelectProfiles, filterProfilesByCountry]);

  const search = (searchTerm) => {
    setSearchName(searchTerm);
    filterProfileByEmailAndName(searchTerm, country);
  };

  const changeOptionSendTo = useCallback(
    (option) => {
      setOptionSendTo(option);
      if (optionSendTo === options[0] && propagateOptionAll) {
        propagateOptionAll();
      }
      if (childRef.current && childRef?.current.updateCountry) {
        childRef.current.updateCountry('');
      }
      setForceUpdate(forceUpdate + 1);
    },
    [setOptionSendTo, optionSendTo, forceUpdate, options, propagateOptionAll],
  );

  const getProfiles = useCallback(() => {
    if (optionSendTo === options[2]) {
      return selectProfiles;
    }
    return getFilteredProfiles();
  }, [optionSendTo, options, selectProfiles, getFilteredProfiles]);

  useImperativeHandle(ref, () => ({
    getProfiles,
    getScope: () => optionSendTo,
    changeToCountryOption: () => changeOptionSendTo(options[1]),
    getCountry: () => country,
    getState: () => state,
  }));

  const renderSearchByText = () => (
    <div>
      <input
        id="searchName"
        type="text"
        className="validate"
        value={searchName}
        onChange={(e) => search(e.target.value)}
      />
      <label htmlFor="searchName">User name</label>
    </div>
  );

  const renderUsers = () => (
    <div>
      <h6>Select Users</h6>
      <div className="scheme-container-large">
        {filteredProfiles.map((profile) => (
          <ProfileItem
            key={profile.id}
            profile={profile}
            showQuestionStatus={showQuestionStatus}
            triviaState={triviaState}
            checked={selectProfiles.includes(profile)}
            setValue={() => {
              selectProfile(profile);
              setForceUpdate(forceUpdate + 1);
            }}
          />
        ))}
      </div>
    </div>
  );

  const renderStateOption = () => {
    if (optionSendTo === options[0]) {
      return <div />;
    }

    return (
      <div>
        <RadioButton name={'all states'} checked={allState} option={'All States'} onChange={() => setAllState(true)} />
        <RadioButton
          name={'Select State'}
          checked={!allState}
          option={'Select State'}
          onChange={() => setAllState(false)}
        />
      </div>
    );
  };

  const renderSelectRegion = () => {
    return (
      <div>
        <SelectCountry ref={childRef} changeCountry={setCountry} />
        {!allState ? <SelectState country={country} changeState={setState} /> : <div />}
        {!country ? <div /> : renderStateOption()}
      </div>
    );
  };

  const renderFilters = () => (
    <div>
      {optionSendTo === options[1] || optionSendTo === options[2] ? renderSelectRegion() : <div />}
      {optionSendTo === options[2] ? renderSearchByText() : <div />}
      {optionSendTo === options[2] && country ? renderUsers() : <div />}
    </div>
  );

  return (
    <div>
      <div className="row">
        <RadioButton
          name={'all'}
          checked={optionSendTo === options[0]}
          option={options[0]}
          onChange={changeOptionSendTo}
        />
        <RadioButton
          name={'country'}
          checked={optionSendTo === options[1]}
          option={options[1]}
          onChange={changeOptionSendTo}
        />
        <RadioButton
          name={'users'}
          checked={optionSendTo === options[2]}
          option={options[2]}
          onChange={changeOptionSendTo}
        />
      </div>
      {renderFilters()}
    </div>
  );
});

export default SelectUsers;
